'use client';

import React, { useMemo } from 'react';
import ReactPlayer from 'react-player';
import { VideoPlayerProps } from './types';
import { useVideoPlayerState, useVideoPlayerChildren } from './hooks';
import { getMergedClassNames } from './utils';
import { VIDEO_PLAYER_CONSTANTS } from './constants';

const VideoPlayer: React.FC<VideoPlayerProps> = React.memo(
  ({
    video,
    classNames = {},
    autoPlay = false,
    muted = false,
    rounded = false,
    loop = false,
    hideNativeControls = false,
    playInBackground = false,
    testId = VIDEO_PLAYER_CONSTANTS.DEFAULT_TEST_ID,
    onReady = () => {},
    onStart = () => {},
    onPlay = () => {},
    onPause = () => {},
    onEnded = () => {},
    children,
  }) => {
    // Memoized computed values
    const mergedClassNames = useMemo(() => getMergedClassNames(classNames, rounded), [classNames, rounded]);

    const {
      setPlayerRef,
      playerId,
      // Optimized computed values and state objects
      videoPlayerState,
      playerConfig,
      wrapperProps,
      viewportProps,
    } = useVideoPlayerState({
      video,
      autoPlay,
      playInBackground,
      onReady,
      onStart,
      onPlay,
      onPause,
      onEnded,
      testId,
      muted,
      loop,
      hideNativeControls,
      mergedClassNames,
    });

    // Custom hook for child rendering - memoized to prevent unnecessary re-renders
    const renderChildren = useVideoPlayerChildren(children, videoPlayerState);

    return (
      <div {...wrapperProps} data-video-player-id={playerId}>
        {/* Video Player Viewport */}
        <div {...viewportProps}>
          {/* React Player */}
          {playerConfig && (
            <>
              <ReactPlayer ref={setPlayerRef} {...playerConfig} playsInline />
            </>
          )}

          {/* Render children with appropriate videoPlayerState */}
          {renderChildren()}
        </div>
      </div>
    );
  },
);

VideoPlayer.displayName = 'VideoPlayer';

export default VideoPlayer;
